import { RefObject } from 'preact';
import { useRef, useCallback, useState } from 'preact/hooks';
import { useId } from '#hooks/UNSAFE_useId';
import { useUser } from '#hooks/UNSAFE_useUser';
import { useTranslationBundle } from '#/hooks/UNSAFE_useTranslationBundle';
import { getClientHints } from '#utils/PRIVATE_clientHints';
import { getItemKeysArray } from '#utils/PRIVATE_collectionUtils';
import { BundleType } from '#resources/nls/bundle';
import { ReorderProps } from '#UNSAFE_Collection/Reorderable.types';

type useReorderableProps<K> = ReorderProps<K> & {
  rootRef: RefObject<HTMLDivElement>;
  itemSelector: string;
  isDisabled?: boolean;
  columns?: number;
};

/**
 * Implements keyboard reorder behavior for list-like components, e.g. TabBar, ListView, CardView
 */
export function useReorderable<K extends string | number>(options: useReorderableProps<K>) {
  const { onReorder, rootRef, itemSelector, isDisabled, columns } = options;

  const currentItemKey = useRef<K | undefined>();
  const { direction } = useUser();
  const isRtl = direction === 'rtl';
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const reorderInstructionsId = 'reorderable_' + useId();
  const { deviceType, platform } = getClientHints();
  const keyPressed =
    platform === 'windows' ? translations.accControlKey() : translations.accCommandKey();
  const reorderInstructions =
    deviceType === 'phone' || deviceType === 'tablet'
      ? ''
      : translations.accReorderInstructions({
          keyPressed: keyPressed
        });
  const [reorderStatus, setReorderStatus] = useState<string | undefined>('');

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const keyHandlers = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
      if (
        (e.metaKey || e.ctrlKey) &&
        e.shiftKey &&
        keyHandlers.includes(e.key) &&
        onReorder &&
        rootRef.current
      ) {
        if (e.metaKey) {
          e.preventDefault();
        }
        const itemKeysArray = getItemKeysArray(rootRef.current, itemSelector);
        const newArray = [...itemKeysArray];
        const lastIndex = newArray.length - 1;
        const currentItemIndex = newArray.indexOf(currentItemKey.current);
        // get reference item index and reordered keys
        const referenceItemIndex = getReferenceItemIndex(
          e.key,
          isRtl,
          currentItemIndex,
          lastIndex,
          columns
        );
        if (referenceItemIndex !== undefined && currentItemIndex !== referenceItemIndex) {
          const referenceKey = newArray[referenceItemIndex]?.toString();
          newArray.splice(currentItemIndex, 1);
          newArray.splice(referenceItemIndex as number, 0, currentItemKey.current);
          onReorder?.({
            reorderedKeys: newArray as K[],
            itemKeys: [currentItemKey.current as K],
            // If the dragged items were dropped at the very beginning, the referenceKey will be null.
            referenceKey: referenceItemIndex === 0 ? null : (newArray[referenceItemIndex - 1] as K)
          });
          // set acc status
          const isPrev = e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? true : false;
          const position = isPrev
            ? translations.accPositionBefore()
            : translations.accPositionAfter();
          const itemKeyString = currentItemKey?.current?.toString();
          if (referenceKey && position && itemKeyString) {
            setReorderStatus(
              translations.accAnnounceMovedKey({
                itemKey: itemKeyString,
                position: position,
                referenceKey: referenceKey
              })
            );
          }
        }
      }
    },
    [currentItemKey, onReorder, columns, isRtl, itemSelector, rootRef, translations]
  );

  const eventProps = isDisabled ? {} : { onKeyDown };

  return {
    reorderProps: eventProps,
    reorderContext: {
      currentItemKey
    },
    reorderInstructionsId,
    reorderInstructions,
    reorderStatus
  } as const;
}

/**
 * A helper function to get referenceItemIndex during keyboard reordering
 *
 * @param eventKey the arrow event keys
 * @param isRtl whether RTL is enabled
 * @param currentItemIndex the index of current item
 * @param lastIndex the last index
 * @param columns optional param to specify the number of columns, for keyboard reordering by arrow up/down keys.
 */
const getReferenceItemIndex = (
  eventKey: string,
  isRtl: boolean,
  currentItemIndex: number,
  lastIndex: number,
  columns?: number
) => {
  const leftDropIndex = currentItemIndex > 0 ? currentItemIndex - 1 : 0;
  const rightDropIndex = currentItemIndex < lastIndex ? currentItemIndex + 1 : lastIndex;
  switch (eventKey) {
    case 'ArrowLeft':
      return isRtl ? rightDropIndex : leftDropIndex;
    case 'ArrowRight':
      return isRtl ? leftDropIndex : rightDropIndex;
    case 'ArrowUp':
      if (columns) {
        // if the current item is at the top row, arrow up should not move anywhere
        return currentItemIndex < columns ? currentItemIndex : currentItemIndex - columns;
      }
    case 'ArrowDown':
      if (columns) {
        // if the current item is at the bottom row, arrow down should not move anywhere
        return currentItemIndex > lastIndex - columns
          ? currentItemIndex
          : currentItemIndex + columns;
      }
    default:
      return undefined;
  }
};
